/**
 * AnimalShogi 盤 (3 列 × 4 行) コンポーネント。
 *
 * 入力は 2 段階:
 * 1. 自分の駒 / 持ち駒をタップ → `selected` 状態で「移動先候補」をハイライト
 * 2. 移動先候補をタップ → `onMove({ Step | Drop })` を発火
 *
 * 合法手はサーバ確定の `legal` 配列 (WASM 経由) を頼りに、 タップ時の絞り込みを行う。
 * 直前の一手 (`lastMove`) があればその移動元/移動先のセルにリング表示。
 */

import { type CSSProperties, useEffect, useRef, useState } from 'react';
import {
  type AnimalMove,
  type AnimalPiece,
  type AnimalState,
  type PieceOnBoard,
  type Side,
} from '../../games/animal';
import { useFreshLastMove } from '../../lib/first-paint';
import { AnimalPieceGlyph } from './AnimalPiece';
import { BoardCoords } from './BoardCoords';

interface Props {
  state: AnimalState;
  legal?: AnimalMove[];
  yourSide?: Side | null;
  /** 盤の向き。 省略時は `yourSide`。 */
  viewFrom?: Side;
  /** _view.allowed_boards が現時点で着手を許可しているか。 engine cs と乖離するケース
   *  (Parallel 多盤の collective turn 中など) で server reject を防ぐため、 必ず
   *  この prop を真の手番判定として使う。 未指定時は engine cs ベースに fallback。 */
  myTurnAllowed?: boolean;
  onMove?: (mv: AnimalMove) => void;
  mini?: boolean;
  lastMove?: AnimalMove;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル用の強調マス。 */
  tutorialHighlight?: [number, number][];
  /** 答え/チュートリアル用: 打つ持ち駒 (AnimalPiece 名) を駒台側で強調する。 */
  tutorialHandHighlight?: string | null;
}

type Selection =
  | { kind: 'cell'; row: number; col: number }
  // 同じ種類の駒を 2 つ持てるので、 種類だけだと両方が選択状態になる。
  // どれを掴んだかは駒台の index で持つ (打つ手自体は種類しか要らない)。
  | { kind: 'hand'; piece: AnimalPiece; index: number };

const ROWS = 4;
const COLS = 3;

export function AnimalBoard({
  state,
  legal,
  yourSide,
  viewFrom,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
  tutorialHandHighlight,
}: Props) {
  const [selected, setSelected] = useState<Selection | null>(null);
  // 終局後 (outcome 立) は yourSide を維持して視点反転を防ぎつつ、 入力は無効化。
  // 盤を開いた瞬間は駒を滑らせない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  const isMyTurn =
    state.outcome == null &&
    (myTurnAllowed !== undefined
      ? myTurnAllowed
      : yourSide != null && yourSide === state.side);

  // 直前 render の state を保持 (= 取りアニメで「捕られた駒」 を一瞬残すため)。
  const prevStateRef = useRef<AnimalState | null>(null);
  const prevState = prevStateRef.current;
  useEffect(() => {
    prevStateRef.current = state;
  }, [state]);

  // last-to セルにおいて、 直前 state の駒 vs 現 state の駒の owner が異なれば capture。
  // → captured ピースのスナップショットを保持し、 cell 上にフェードアウト演出で重ねる。
  let capturedAt: { row: number; col: number; piece: PieceOnBoard } | null = null;
  if (
    !mini &&
    prevState &&
    lastMove &&
    'Step' in lastMove
  ) {
    const [toR, toC] = lastMove.Step.to;
    const prev = prevState.board[toR]?.[toC];
    const curr = state.board[toR]?.[toC];
    if (prev && curr && prev.owner !== curr.owner) {
      capturedAt = { row: toR, col: toC, piece: prev };
    }
  }

  const candidatesForSelection = (sel: Selection | null): Set<string> => {
    if (!sel || !legal) return new Set();
    const out = new Set<string>();
    for (const mv of legal) {
      if ('Step' in mv && sel.kind === 'cell') {
        if (mv.Step.from[0] === sel.row && mv.Step.from[1] === sel.col) {
          out.add(`${mv.Step.to[0]}-${mv.Step.to[1]}`);
        }
      } else if ('Drop' in mv && sel.kind === 'hand') {
        if (mv.Drop.piece === sel.piece) {
          out.add(`${mv.Drop.to[0]}-${mv.Drop.to[1]}`);
        }
      }
    }
    return out;
  };

  const candidates = candidatesForSelection(selected);

  // 直前の一手の from/to を Set 化
  const lastKeys: { from?: string; to?: string } = (() => {
    if (!lastMove || mini) return {};
    if ('Step' in lastMove) {
      return {
        from: `${lastMove.Step.from[0]}-${lastMove.Step.from[1]}`,
        to: `${lastMove.Step.to[0]}-${lastMove.Step.to[1]}`,
      };
    }
    if ('Drop' in lastMove) {
      return { to: `${lastMove.Drop.to[0]}-${lastMove.Drop.to[1]}` };
    }
    return {};
  })();

  const onCell = (row: number, col: number, cell: PieceOnBoard | null) => {
    if (mini || !isMyTurn) return;
    if (selected && candidates.has(`${row}-${col}`)) {
      if (selected.kind === 'cell') {
        onMove?.({ Step: { from: [selected.row, selected.col], to: [row, col] } });
      } else {
        onMove?.({ Drop: { piece: selected.piece, to: [row, col] } });
      }
      setSelected(null);
      return;
    }
    if (cell && cell.owner === state.side) {
      setSelected({ kind: 'cell', row, col });
    } else {
      setSelected(null);
    }
  };

  const onHand = (piece: AnimalPiece, index: number) => {
    if (mini || !isMyTurn) return;
    setSelected({ kind: 'hand', piece, index });
  };

  // 自分の側に応じて盤を 180 度回転 (相手側を上)。 行だけでなく列も反転しないと
  // 上下ミラーになり、 左右非対称な初期配置 (キリン/ゾウ) が後手視点で逆に見える。
  const viewSide = viewFrom ?? yourSide;
  const flip = viewSide === 'Second';
  /** 手番側の持ち駒が盤の奥にある。 */
  const topHandActive = viewFrom != null && yourSide !== viewFrom;
  const rowOrder = flip
    ? Array.from({ length: ROWS }, (_, i) => i) // Guest 視点: row=0 が手前
    : Array.from({ length: ROWS }, (_, i) => ROWS - 1 - i); // Host 視点: row=3 が奥
  const colOrder = flip
    ? Array.from({ length: COLS }, (_, i) => COLS - 1 - i) // Guest 視点: col=2 が左
    : Array.from({ length: COLS }, (_, i) => i); // Host 視点: col=0 が左

  const myHand = state.hands[viewSide === 'Second' ? 1 : 0];
  const oppHand = state.hands[viewSide === 'Second' ? 0 : 1];

  // yourSide が null (= 観戦) のとき、 mine/opp は Host/Guest にフォールバック。
  const ownerVariant = (owner: Side): 'mine' | 'opp' => {
    if (viewSide == null) return owner === 'First' ? 'mine' : 'opp';
    return owner === viewSide ? 'mine' : 'opp';
  };

  // 「自分の手番ではない」 状態を視覚的に明示。 Parallel では他盤を打たないと
  // 順番が回ってこないので、 ここを click しても何も起きないことを伝える必要がある。
  const turnHint: 'opp' | null =
    !mini && !staticView && !isMyTurn && state.outcome == null ? 'opp' : null;

  return (
    <div
      className={`animal-frame ${mini ? 'mini' : ''} ${turnHint === 'opp' ? 'not-my-turn' : ''}`}
    >
      <div className="animal-hand opp">
        {oppHand.map((p, i) => topHandActive ? (
          <button
            key={i}
            className={`hand-piece opp ${selected?.kind === 'hand' && selected.index === i ? 'sel' : ''}`}
            onClick={() => onHand(p, i)}
            disabled={mini || !isMyTurn}
            data-testid="hand-opp-piece"
          >
            <AnimalPieceGlyph kind={p} variant="opp" flip hand />
          </button>
        ) : (
          <span key={i} className="hand-piece opp" data-testid="hand-opp-piece">
            <AnimalPieceGlyph kind={p} variant="opp" flip hand />
          </span>
        ))}
      </div>
      <div className="animal-board">
        {/* 座標は升の外 (盤の縁) に。 升の中だと駒に隠れる。 表示順で渡すので反転も追従する。 */}
        {!mini && (
          <BoardCoords
            files={colOrder.map((c) => String.fromCharCode(65 + c))}
            ranks={rowOrder.map((r) => `${r + 1}`)}
          />
        )}
        {rowOrder.flatMap((r) =>
          colOrder.map((c) => {
            const cell = state.board[r][c];
            const k = `${r}-${c}`;
            const isCand = candidates.has(k);
            const isSelected =
              selected?.kind === 'cell' && selected.row === r && selected.col === c;
            const lastClass =
              lastKeys.to === k ? 'last-to' : lastKeys.from === k ? 'last-from' : '';
            // 着手アニメ用 offset。 last-to のセルでは「from の方向」 から
            // スライドインさせる (= 駒が動いた方向の相対 cell offset)。
            // engine 座標 → display 座標に変換してから差を取る。 Guest 視点は
            // 行・列とも反転 (180 度回転) している点に注意。
            let pieceAnimStyle: CSSProperties | undefined;
            if (lastKeys.to === k && lastMove && 'Step' in lastMove) {
              const fromR = lastMove.Step.from[0];
              const fromC = lastMove.Step.from[1];
              const toR = r;
              const toC = c;
              const dispFromR = flip ? fromR : ROWS - 1 - fromR;
              const dispToR = flip ? toR : ROWS - 1 - toR;
              const dispFromC = flip ? COLS - 1 - fromC : fromC;
              const dispToC = flip ? COLS - 1 - toC : toC;
              const dy = dispFromR - dispToR;
              const dx = dispFromC - dispToC;
              pieceAnimStyle = {
                ['--a-anim-dx' as string]: `${dx * 100}%`,
                ['--a-anim-dy' as string]: `${dy * 100}%`,
              };
            } else if (lastKeys.to === k && lastMove && 'Drop' in lastMove) {
              // Drop は 「上から落ちてくる」 軽い演出
              pieceAnimStyle = {
                ['--a-anim-dx' as string]: '0%',
                ['--a-anim-dy' as string]: '-60%',
              };
            }
            const isTutorialHint =
              tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
            return (
              <button
                key={k}
                className={`a-cell ${isCand ? 'cand' : ''} ${isSelected ? 'sel' : ''} ${lastClass} ${
                  isTutorialHint ? 'tutorial-hint' : ''
                }`}
                onClick={() => onCell(r, c, cell)}
                disabled={mini}
                aria-label={`row ${r} col ${c}`}
              >
                {/* 取られた駒の幻 (last-to セル限定): 直前 state の駒を一瞬重ね、
                    fade + scale で消える。 通常の slide-in より少し短く設定し、
                    捕った駒の到着が「先に消す」 形でつなぐ。 */}
                {capturedAt &&
                  capturedAt.row === r &&
                  capturedAt.col === c && (
                    <span className="a-piece-wrap piece-captured" aria-hidden>
                      <AnimalPieceGlyph
                        kind={capturedAt.piece.kind}
                        variant={ownerVariant(capturedAt.piece.owner)}
                        flip={capturedAt.piece.owner !== (viewSide ?? 'First')}
                      />
                    </span>
                  )}
                {cell && (
                  <span
                    className={`a-piece-wrap ${lastClass === 'last-to' && freshLast ? 'piece-anim' : ''}`}
                    style={pieceAnimStyle}
                  >
                    <AnimalPieceGlyph
                      kind={cell.kind}
                      variant={ownerVariant(cell.owner)}
                      flip={cell.owner !== (viewSide ?? 'First')}
                    />
                  </span>
                )}
              </button>
            );
          }),
        )}
      </div>
      <div className="animal-hand my">
        {myHand.map((p, i) => (
          <button
            key={i}
            className={`hand-piece my ${
              selected?.kind === 'hand' && selected.index === i ? 'sel' : ''
            } ${tutorialHandHighlight === p ? 'tutorial-hint' : ''}`}
            onClick={() => onHand(p, i)}
            disabled={mini || !isMyTurn || topHandActive}
            data-testid="hand-my-piece"
          >
            <AnimalPieceGlyph kind={p} variant="mine" hand />
          </button>
        ))}
      </div>
      {turnHint === 'opp' && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}
